/**
 * Une entreprise de l'annuaire public, traduite dans la langue du dossier.
 *
 * L'annuaire (recherche-entreprises.api.gouv.fr, tenu par l'État, gratuit et
 * sans clé) donne la forme juridique, le code APE, la date de début et la
 * commune. Le dossier en tire trois de ses six réponses : le statut,
 * l'activité, la date de début. Le reste ne se lit nulle part : micro ou
 * réel pour un entrepreneur individuel, et bien sûr le chiffre d'affaires.
 *
 * DEVINER JUSTE OU NE RIEN DIRE. Comme les traducteurs du dossier : un code
 * APE qui ne tranche pas entre deux activités ne renvoie rien, et le
 * lecteur choisit. Une forme juridique que les outils ne couvrent pas (une
 * SARL à plusieurs associés, une association) n'est pas forcée dans une
 * case : `prisEnCharge` le dit, et la page propose les questions.
 *
 * Module pur : il se teste seul (scripts/check-dossier.mjs).
 */

/**
 * La forme juridique (nomenclature INSEE, catégorie à quatre chiffres).
 *   1000 : entrepreneur individuel (micro ou réel, l'annuaire ne le dit pas)
 *   5498 : SARL unipersonnelle, donc EURL
 *   5499 : SARL sans autre indication : l'Insee y range aussi bien des EURL
 *          que des SARL à plusieurs associés, le lecteur dit s'il est seul
 *          (`sarl`) ; c'est la forme de la plupart des petites SARL récentes
 *   5720 : SAS unipersonnelle, donc SASU
 *   5710 : SAS, souvent à associé unique chez un solopreneur : traitée en SASU
 */
export function statutDepuisNature(nature) {
    const n = String(nature || "");
    if (n === "1000") return { statut: null, individuel: true, prisEnCharge: true };
    if (n === "5498") return { statut: "eurl", individuel: false, prisEnCharge: true };
    if (n === "5499") return { statut: null, individuel: false, sarl: true, prisEnCharge: true };
    if (n === "5720" || n === "5710") return { statut: "sasu", individuel: false, prisEnCharge: true };
    return { statut: null, individuel: false, prisEnCharge: false };
}

/**
 * L'activité d'après le code APE (NAF rév. 2, « 70.22Z »). Les grandes
 * familles seulement ; un code hors liste ne renvoie rien.
 *
 * `artisan` n'est vrai que pour les métiers typiques de l'artisanat
 * (bâtiment, réparation, coiffure) : l'inscription réelle au registre ne se
 * lit pas dans l'annuaire, le lecteur peut corriger.
 */
const REGLES_APE = [
    // Professions libérales réglementées affiliées à la Cipav, ou à statut
    // proche pour les calculs de la micro : architectes, psychologues,
    // ostéopathes et autres praticiens non conventionnés.
    { prefixes: ["71.11"], activite: "cipav" },
    { prefixes: ["86.90"], activite: "cipav" },
    // Libéral : conseil, informatique, études, création, enseignement, arts.
    {
        prefixes: ["58.2", "59", "60", "62", "63.1", "63.9", "69", "70.2", "71.12", "72", "73", "74", "78", "82.1", "85.5", "85.6", "90", "93.1"],
        activite: "bnc",
    },
    // Artisanat : bâtiment, réparation, soins de la personne.
    { prefixes: ["41", "42", "43", "95", "96.02", "96.04", "96.09"], activite: "services", artisan: true },
    // Prestations commerciales : transport, livraison, nettoyage, location.
    { prefixes: ["49", "52", "53", "77", "79", "80", "81", "82.9", "96.01", "96.03"], activite: "services", artisan: false },
    // Vente : commerce, fabrication, restauration, hébergement.
    { prefixes: ["10", "11", "13", "14", "15", "16", "18", "20", "22", "23", "25", "31", "32", "45", "46", "47", "55", "56"], activite: "vente" },
];

export function activiteDepuisApe(ape) {
    const code = String(ape || "").trim().toUpperCase();
    if (!/^\d{2}\.\d{2}[A-Z]$/.test(code)) return {};
    // Le préfixe le plus long l'emporte : 71.11 (architecte) avant 71.
    let meilleure = null;
    for (const regle of REGLES_APE) {
        for (const p of regle.prefixes) {
            if (code.startsWith(p) && (!meilleure || p.length > meilleure.longueur)) meilleure = { regle, longueur: p.length };
        }
    }
    if (!meilleure) return {};
    const { activite, artisan } = meilleure.regle;
    return artisan === undefined ? { activite } : { activite, artisan };
}

const DATE_ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Un résultat de l'annuaire, réduit à ce que la page affiche et à ce que le
 * dossier peut garder.
 *
 * DE QUOI RECONNAÎTRE SON ENTREPRISE, PAS DE QUOI LA FICHER. La fiche montre
 * au lecteur ce que l'État sait déjà de lui : le numéro de TVA, le nom
 * commercial, le département, les marques officielles (Qualiopi, RGE…), le
 * dernier chiffre d'affaires publié par une société. Du dirigeant sortent le
 * prénom, pour le saluer, et « Prénom N. » (`dirigeant`), pour qu'il se
 * reconnaisse au moment de confirmer : jamais son nom entier, ni sa date de
 * naissance, ni l'adresse du siège, souvent le domicile d'un entrepreneur
 * individuel. La recherche est ouverte à tout visiteur, sur n'importe quelle
 * entreprise.
 */
export function entrepriseDepuisAnnuaire(r) {
    if (!r || typeof r !== "object") return null;
    const siege = r.siege || {};
    const nature = statutDepuisNature(r.nature_juridique);
    const ape = siege.activite_principale || r.activite_principale || null;
    const dateDebut = DATE_ISO.test(siege.date_debut_activite || "")
        ? siege.date_debut_activite
        : DATE_ISO.test(r.date_creation || "")
          ? r.date_creation
          : null;
    return {
        nom: nomPropre(String(r.nom_complet || r.nom_raison_sociale || "")).slice(0, 120),
        siren: /^\d{9}$/.test(r.siren || "") ? r.siren : null,
        siret: /^\d{14}$/.test(siege.siret || "") ? siege.siret : null,
        nature: String(r.nature_juridique || ""),
        ...nature,
        ape,
        ...activiteDepuisApe(ape),
        dateDebut,
        commune: siege.libelle_commune || null,
        codePostal: siege.code_postal || null,
        departement: /^\d[\dAB]\d?$/.test(siege.departement || "") ? siege.departement : null,
        nomCommercial: nomCommercialDe(siege, r),
        numeroTva: Array.isArray(r.tva) && /^FR[\dA-Z]{2}\d{9}$/.test(r.tva[0] || "") ? r.tva[0] : null,
        prenom: prenomDuDirigeant(r),
        dirigeant: dirigeantCourt(r),
        marques: marquesDe(r.complements),
        caPublie: dernierCaPublie(r.finances),
        active: r.etat_administratif !== "F",
    };
}

/**
 * « CAMILLE MARTIN (CAMILLE MARTIN) » → « CAMILLE MARTIN ». L'annuaire colle
 * entre parenthèses la dénomination ou l'enseigne, souvent identique au nom,
 * ou « EI » suivi du nom retourné : on n'affiche pas deux fois la même chose.
 */
const memesMots = (a, b) => {
    const mots = (x) => String(x).toUpperCase().replace(/\(.*\)/, "").trim().replace(/^EI\s+/, "").split(/\s+/).sort().join(" ");
    return mots(a) === mots(b);
};

function nomPropre(nom) {
    const m = nom.trim().match(/^(.+?)\s*\((.+)\)$/);
    if (!m) return nom.trim();
    const [, dehors, dedans] = m;
    return memesMots(dehors, dedans) ? dehors : nom.trim();
}

/** « CAMILLE PATISSERIE », quand l'enseigne n'est pas déjà le nom. */
function nomCommercialDe(siege, r) {
    const brut = siege.nom_commercial || (Array.isArray(siege.liste_enseignes) ? siege.liste_enseignes[0] : null);
    if (!brut) return null;
    const nom = String(r.nom_complet || r.nom_raison_sociale || "").toUpperCase();
    const enseigne = String(brut).trim().slice(0, 80);
    return enseigne && !nom.startsWith(enseigne.toUpperCase()) && !memesMots(nom, enseigne) ? enseigne : null;
}

/** Le premier dirigeant qui est une personne, pas une société. */
function premierDirigeant(r) {
    return (Array.isArray(r.dirigeants) ? r.dirigeants : []).find((x) => x && x.type_dirigeant === "personne physique" && x.prenoms) || null;
}

/** « Louis-Nicolas L. » : de quoi se reconnaître, sans afficher le nom entier. */
function dirigeantCourt(r) {
    const prenom = prenomDuDirigeant(r);
    if (!prenom) return null;
    const initiale = String(premierDirigeant(r).nom || "").trim().charAt(0).toUpperCase();
    return /\p{L}/u.test(initiale) ? `${prenom} ${initiale}.` : prenom;
}

/** Le prénom du premier dirigeant personne physique : « LOUIS-NICOLAS » → « Louis-Nicolas ». */
function prenomDuDirigeant(r) {
    const dirigeant = premierDirigeant(r);
    if (!dirigeant) return null;
    const premier = String(dirigeant.prenoms).trim().split(/\s+/)[0];
    if (!/^[\p{L}'-]{2,30}$/u.test(premier)) return null;
    return premier.toLowerCase().replace(/(^|[-'])(\p{L})/gu, (_, sep, l) => sep + l.toUpperCase());
}

/** Les marques officielles qu'un client peut vérifier : on les montre telles quelles. */
function marquesDe(c) {
    if (!c || typeof c !== "object") return [];
    return [
        c.est_qualiopi ? "qualiopi" : c.est_organisme_formation ? "formation" : null,
        c.est_rge ? "rge" : null,
        c.est_ess ? "ess" : null,
        c.est_societe_mission ? "mission" : null,
        c.est_bio ? "bio" : null,
    ].filter(Boolean);
}

/** Le dernier chiffre d'affaires déposé au greffe : { annee, ca }, pour les sociétés qui publient leurs comptes. */
function dernierCaPublie(finances) {
    if (!finances || typeof finances !== "object") return null;
    const annees = Object.keys(finances).filter((a) => /^\d{4}$/.test(a) && Number.isFinite(finances[a]?.ca) && finances[a].ca > 0).sort();
    const annee = annees[annees.length - 1];
    return annee ? { annee: Number(annee), ca: Math.round(finances[annee].ca) } : null;
}

/** Ce que le dossier garde d'une entreprise choisie. */
export function dossierDepuisEntreprise(e) {
    if (!e) return {};
    const champs = {};
    if (e.statut) champs.statut = e.statut;
    if (e.activite) champs.activite = e.activite;
    if (typeof e.artisan === "boolean") champs.artisan = e.artisan;
    if (e.dateDebut) champs.dateDebut = e.dateDebut;
    // Son nom et son SIREN : choisir une autre entreprise remplace les deux,
    // même quand la nouvelle n'a pas de SIREN lisible.
    champs.entrepriseNom = e.nom || null;
    champs.siren = /^\d{9}$/.test(e.siren || "") ? e.siren : null;
    return champs;
}

/**
 * L'entreprise choisie, gardée sur l'appareil pour la reconnaître au retour
 * (« Modifier », « Supprimer » en haut de /outils). Ce sont des données
 * publiques de l'annuaire. Seuls son nom et son SIREN partent avec le dossier
 * du compte (dossierDepuisEntreprise) ; le reste de la fiche ne quitte pas
 * l'appareil.
 */
export const CLE_ENTREPRISE = "believemy-entreprise";

const TEXTES_ENTREPRISE = ["nom", "siren", "siret", "nature", "statut", "ape", "apeLibelle", "activite", "dateDebut", "commune", "codePostal", "departement", "nomCommercial", "numeroTva", "prenom", "dirigeant"];
const BOOLEENS_ENTREPRISE = ["individuel", "sarl", "prisEnCharge", "artisan"];

export function nettoyerEntreprise(brut) {
    if (!brut || typeof brut !== "object" || typeof brut.nom !== "string" || !brut.nom) return null;
    const e = {};
    for (const c of TEXTES_ENTREPRISE) e[c] = typeof brut[c] === "string" ? brut[c].slice(0, 160) : null;
    for (const c of BOOLEENS_ENTREPRISE) if (typeof brut[c] === "boolean") e[c] = brut[c];
    e.marques = Array.isArray(brut.marques) ? brut.marques.filter((m) => typeof m === "string").slice(0, 6) : [];
    e.caPublie = brut.caPublie && Number.isFinite(brut.caPublie.annee) && Number.isFinite(brut.caPublie.ca) ? { annee: brut.caPublie.annee, ca: brut.caPublie.ca } : null;
    return e;
}
